import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  InputAdornment,
  IconButton
} from '@mui/material';
import { styled } from '@mui/system';
import LockIcon from '@mui/icons-material/Lock';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import axios from 'axios';
import { getGroupTheme } from '../groups';

// Função para validar senha com critérios específicos
function validatePassword(password: string): { isValid: boolean; message: string } {
  if (password.length < 6) {
    return {
      isValid: false,
      message: 'A senha deve ter pelo menos 6 caracteres'
    };
  }

  if (!/[A-Z]/.test(password)) {
    return {
      isValid: false,
      message: 'A senha deve conter pelo menos 1 letra maiúscula'
    };
  }

  if (!/[a-z]/.test(password)) {
    return {
      isValid: false,
      message: 'A senha deve conter pelo menos 1 letra minúscula'
    };
  }

  if (!/[0-9]/.test(password)) {
    return {
      isValid: false,
      message: 'A senha deve conter pelo menos 1 número'
    };
  }

  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
    return {
      isValid: false,
      message: 'A senha deve conter pelo menos 1 caractere especial'
    };
  }

  return {
    isValid: true,
    message: 'Senha válida'
  };
}

// Cria uma instância do axios específica para auth (sem interceptors)
const authApi = axios.create({
  baseURL: `${process.env.REACT_APP_SCALAR_API_URL}/auth`,
  timeout: 10000,
});

const Container = styled(Box)<{ backgroundGradient: string }>`
  min-height: 100vh;
  background: ${props => props.backgroundGradient};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const StyledCard = styled(Card)`
  max-width: 450px;
  width: 100%;
  border-radius: 16px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
`;

const LogoBox = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 32px;
`;

const Logo = styled('img')`
  max-width: 180px;
  height: auto;
`;

const StyledTextField = styled(TextField)`
  margin-bottom: 20px;
  
  .MuiOutlinedInput-root {
    border-radius: 8px;
  }
`;

const StyledButton = styled(Button)`
  border-radius: 8px;
  padding: 12px;
  font-weight: 600;
  text-transform: none;
  font-size: 16px;
`;

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [tokenValid, setTokenValid] = useState(false);

  // Obtém o tema do grupo baseado na URL
  const groupTheme = getGroupTheme();

  useEffect(() => {
    validateToken();
  }, [token]);

  const validateToken = async () => {
    if (!token) {
      setError('Token de recuperação não encontrado na URL');
      setValidating(false);
      return;
    }

    try {
      const response = await authApi.get(`/validate-token/${token}`);
      
      if (response.data.valid) {
        setTokenValid(true);
      } else {
        setError('Token inválido ou expirado. Solicite uma nova recuperação de senha.');
      }
    } catch (err: any) {
      setError('Erro ao validar token de recuperação.');
    } finally {
      setValidating(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newPassword || !confirmPassword) {
      setError('Preencha todos os campos');
      return;
    }

    // Validação completa da senha
    const passwordValidation = validatePassword(newPassword);
    if (!passwordValidation.isValid) {
      setError(passwordValidation.message);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('As senhas não coincidem');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await authApi.post('/reset-password', {
        token,
        newPassword,
        confirmPassword
      });

      if (response.data.success) {
        setSuccess(true);
      } else {
        setError(response.data.message || 'Erro ao redefinir senha');
      }
    } catch (err: any) {
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('Erro interno do servidor. Tente novamente mais tarde.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/');
  };

  const renderValidating = () => (
    <Box textAlign="center" py={4}>
      <CircularProgress size={48} sx={{ mb: 3, color: groupTheme.theme.primaryColor }} />
      <Typography variant="h6" gutterBottom>
        Validando token...
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Por favor, aguarde enquanto verificamos seu token de recuperação.
      </Typography>
    </Box>
  );

  const renderInvalidToken = () => (
    <Box textAlign="center" py={2}>
      <LockIcon color="error" sx={{ fontSize: 48, mb: 2 }} />
      <Typography variant="h6" gutterBottom color="error">
        Token Inválido
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        O token de recuperação é inválido ou já expirou.
      </Typography>
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
      <StyledButton
        fullWidth
        variant="outlined"
        onClick={handleBackToLogin}
        size="large"
      >
        Voltar ao Login
      </StyledButton>
    </Box>
  );

  const renderSuccess = () => (
    <Box textAlign="center" py={2}>
      <CheckCircleIcon color="success" sx={{ fontSize: 48, mb: 2 }} />
      <Typography variant="h6" gutterBottom>
        Senha Redefinida!
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Sua senha foi redefinida com sucesso. Você já pode fazer login com a nova senha.
      </Typography>
      <StyledButton
        fullWidth
        variant="contained"
        onClick={handleBackToLogin}
        size="large"
        sx={{ 
          backgroundColor: groupTheme.theme.primaryColor,
          '&:hover': {
            backgroundColor: `${groupTheme.theme.primaryColor}dd`
          }
        }}
      >
        Ir para Login
      </StyledButton>
    </Box>
  );

  const renderForm = () => (
    <>
      <Box textAlign="center" mb={3}>
        <LockIcon sx={{ fontSize: 48, mb: 2, color: groupTheme.theme.primaryColor }} />
        <Typography variant="h5" gutterBottom>
          Nova Senha
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Digite sua nova senha abaixo
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <StyledTextField
          fullWidth
          label="Nova Senha"
          type={showPassword ? 'text' : 'password'}
          variant="outlined"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          disabled={loading}
          autoFocus
          required
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* Indicadores de validação da senha */}
        {newPassword && (
          <Box sx={{ mt: 1, mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Critérios da senha:
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: newPassword.length >= 6 ? 'success.main' : 'text.secondary',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5
                }}
              >
                {newPassword.length >= 6 ? '✓' : '○'} Pelo menos 6 caracteres
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: /[A-Z]/.test(newPassword) ? 'success.main' : 'text.secondary',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5
                }}
              >
                {/[A-Z]/.test(newPassword) ? '✓' : '○'} 1 letra maiúscula
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: /[a-z]/.test(newPassword) ? 'success.main' : 'text.secondary',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5
                }}
              >
                {/[a-z]/.test(newPassword) ? '✓' : '○'} 1 letra minúscula
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: /[0-9]/.test(newPassword) ? 'success.main' : 'text.secondary',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5
                }}
              >
                {/[0-9]/.test(newPassword) ? '✓' : '○'} 1 número
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(newPassword) ? 'success.main' : 'text.secondary',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5
                }}
              >
                {/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(newPassword) ? '✓' : '○'} 1 caractere especial
              </Typography>
            </Box>
          </Box>
        )}

        <StyledTextField
          fullWidth
          label="Confirmar Nova Senha"
          type={showConfirmPassword ? 'text' : 'password'}
          variant="outlined"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          disabled={loading}
          required
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  edge="end"
                >
                  {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Box display="flex" gap={2}>
          <StyledButton
            variant="outlined"
            onClick={handleBackToLogin}
            disabled={loading}
            sx={{ 
              flex: 1,
              borderColor: groupTheme.theme.primaryColor,
              color: groupTheme.theme.primaryColor,
              '&:hover': {
                borderColor: groupTheme.theme.primaryColor,
                backgroundColor: `${groupTheme.theme.primaryColor}10`
              }
            }}
            size="large"
          >
            Cancelar
          </StyledButton>
          <StyledButton
            type="submit"
            variant="contained"
            disabled={loading || !newPassword || !confirmPassword}
            sx={{ 
              flex: 1,
              backgroundColor: groupTheme.theme.primaryColor,
              '&:hover': {
                backgroundColor: `${groupTheme.theme.primaryColor}dd`
              }
            }}
            size="large"
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <LockIcon />}
          >
            {loading ? 'Redefinindo...' : 'Redefinir Senha'}
          </StyledButton>
        </Box>
      </form>
    </>
  );

  // Gera o background baseado no tema do grupo
  const getBackgroundGradient = () => {
    const primaryColor = groupTheme.theme.primaryColor;
    const loginBackground = groupTheme.theme.loginRightBackgroundColor;
    
    // Se já é um gradiente, usa ele
    if (loginBackground?.includes('gradient')) {
      return loginBackground;
    }
    
    // Se é uma cor sólida, cria um gradiente baseado nela
    if (loginBackground && loginBackground !== primaryColor) {
      return `linear-gradient(135deg, ${primaryColor} 0%, ${loginBackground} 100%)`;
    }
    
    // Fallback: gradiente baseado na cor primária
    return `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}aa 100%)`;
  };

  return (
    <Container backgroundGradient={getBackgroundGradient()}>
      <StyledCard>
        <CardContent sx={{ p: 4 }}>
          <LogoBox>
            <Logo src={groupTheme.group} alt={`Logo ${groupTheme.theme.tabTitle}`} />
          </LogoBox>
          
          {validating ? renderValidating() : 
           !tokenValid ? renderInvalidToken() :
           success ? renderSuccess() : 
           renderForm()}
        </CardContent>
      </StyledCard>
    </Container>
  );
}
